import type {
    MembershipIncome,
    MembershipIncomeUpsertDto,
} from '@fruitful/member-profile';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    DollarInput,
    IconButton,
    Input,
    MoreHorizontalIcon,
    Spinner,
    SpinnerSize,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { captureException } from '../../../logger/Logger';
import { GroveMoneyMapPickerField } from '../GroveMoneyMapPickerField';
import {
    isFocusMovingWithinMoneyMapTableRow,
    type MoneyMapTableRowBlurEvent,
    useMoneyMapTableRowFocusWithin,
} from '../moneyMapperTableRowFocus';
import {
    formFromIncome,
    INCOME_SAVED_TOAST_DEBOUNCE_MS,
    type IncomeDetailsFormState,
    type IncomeTypePickerItem,
    isTableIncomeFormUnchanged,
    type OwnerPickerItem,
    type PayCyclePickerItem,
} from './moneyMapperIncomeForm';
import {
    isIncomePeriodAmountInvalid,
    isIncomeSourceEmpty,
    persistMembershipIncomeFromForm,
    validateIncomeFormForUpsert,
} from './moneyMapperIncomePersist';
import {
    INCOME_TABLE_ACTIONS_OUTER_WIDTH,
    INCOME_TABLE_ROW_PADDING_HORIZONTAL,
    incomeTableAmountColumnProps,
    incomeTableIncomeTypeColumnProps,
    incomeTableMemberColumnProps,
    incomeTablePayCycleColumnProps,
    incomeTableRowFocusActive,
    incomeTableRowFocusIdle,
    incomeTableSourceColumnProps,
} from './moneyMapperIncomeTableLayout';

export interface MainIncomeTableRowProps {
    income: MembershipIncome;
    membershipId: string;
    ownerPickerItems: OwnerPickerItem[];
    payCyclePickerItems: PayCyclePickerItem[];
    incomeTypePickerItems: IncomeTypePickerItem[];
    canMutate: boolean;
    moneyMapSaving: boolean;
    isModalBusy: boolean;
    onOpenDetails: () => void;
    upsertMembershipIncomeAsync: (
        id: string,
        body: MembershipIncomeUpsertDto[],
    ) => Promise<unknown>;
    refetchMembershipAndSyncMoneyMap: () => Promise<void>;
}

export const MainIncomeTableRow = ({
    income,
    membershipId,
    ownerPickerItems,
    payCyclePickerItems,
    incomeTypePickerItems,
    canMutate,
    moneyMapSaving,
    isModalBusy,
    onOpenDetails,
    upsertMembershipIncomeAsync,
    refetchMembershipAndSyncMoneyMap,
}: MainIncomeTableRowProps) => {
    const toast = useToastController();

    const [draft, setDraft] = useState<IncomeDetailsFormState>(() =>
        formFromIncome(income),
    );
    const [showSourceInvalid, setShowSourceInvalid] = useState(false);
    const [showAmountInvalid, setShowAmountInvalid] = useState(false);
    const [isRowSaving, setIsRowSaving] = useState(false);
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const lastIncomeSavedToastAtRef = useRef(0);
    const tableRowRef = useRef<HTMLElement | null>(null);
    const [tableRowRootElement, setTableRowRootElement] =
        useState<HTMLElement | null>(null);

    const assignTableRowRootRef = useCallback((node: HTMLElement | null) => {
        tableRowRef.current = node;
        setTableRowRootElement(node);
    }, []);

    const hasFocusWithinTableRow =
        useMoneyMapTableRowFocusWithin(tableRowRootElement);

    useEffect(() => {
        setDraft(formFromIncome(income));
        setShowSourceInvalid(false);
        setShowAmountInvalid(false);
    }, [income.id, income.updatedAt]);

    const disabled =
        !canMutate ||
        moneyMapSaving ||
        isModalBusy ||
        isRowSaving ||
        ownerPickerItems.length === 0;

    const showMemberDropdown = ownerPickerItems.length > 1;

    const tryPersist = async (next: IncomeDetailsFormState) => {
        if (!canMutate || moneyMapSaving || isModalBusy || isRowSaving) {
            return;
        }
        const validation = validateIncomeFormForUpsert(next);
        if (!validation.ok) {
            return;
        }

        if (isTableIncomeFormUnchanged(income, next)) {
            return;
        }

        setIsRowSaving(true);
        try {
            await persistMembershipIncomeFromForm({
                upsertMembershipIncomeAsync,
                refetchMembershipAndSyncMoneyMap,
                membershipId,
                existing: income,
                form: next,
            });
            const now = Date.now();
            if (
                now - lastIncomeSavedToastAtRef.current >=
                INCOME_SAVED_TOAST_DEBOUNCE_MS
            ) {
                lastIncomeSavedToastAtRef.current = now;
                toast.show('Income saved', MutationSuccessToastOptions);
            }
        } catch (error) {
            captureException(error);
            toast.show('Error saving income', MutationErrorToastOptions(error));
        } finally {
            setIsRowSaving(false);
        }
    };

    const persistIfLeavingRow = (event: MoneyMapTableRowBlurEvent) => {
        const root = tableRowRef.current as unknown as
            | Element
            | null
            | undefined;
        if (isFocusMovingWithinMoneyMapTableRow(root, event)) {
            return;
        }
        void tryPersist(draftRef.current);
    };

    return (
        <XStack
            ref={assignTableRowRootRef}
            width="100%"
            alignItems="flex-start"
            paddingVertical={12}
            paddingHorizontal={INCOME_TABLE_ROW_PADDING_HORIZONTAL}
            gap={8}
            {...(hasFocusWithinTableRow
                ? incomeTableRowFocusActive
                : incomeTableRowFocusIdle)}
        >
            {showMemberDropdown && (
                <YStack {...incomeTableMemberColumnProps}>
                    <GroveMoneyMapPickerField
                        size="regular"
                        value={draft.belongsToUserId}
                        options={ownerPickerItems}
                        disabled={disabled}
                        onTriggerBlur={persistIfLeavingRow}
                        onChange={(v) => {
                            const next = { ...draft, belongsToUserId: v };
                            setDraft(next);
                            void tryPersist(next);
                        }}
                    />
                </YStack>
            )}
            <YStack {...incomeTableSourceColumnProps}>
                <Input
                    value={draft.description}
                    invalid={showSourceInvalid}
                    onChangeText={(t) => {
                        setDraft((d) => ({ ...d, description: t }));
                        if (t.trim().length > 0) {
                            setShowSourceInvalid(false);
                        }
                    }}
                    onBlur={(e) => {
                        setShowSourceInvalid(
                            isIncomeSourceEmpty(draftRef.current.description),
                        );
                        if (
                            !isFocusMovingWithinMoneyMapTableRow(
                                tableRowRef.current as unknown as Element | null,
                                e,
                            )
                        ) {
                            void tryPersist(draftRef.current);
                        }
                    }}
                    placeholder="Source"
                    disabled={disabled}
                />
            </YStack>
            <YStack {...incomeTableAmountColumnProps}>
                <DollarInput
                    value={draft.periodAmountStr}
                    invalid={showAmountInvalid}
                    onChangeText={(t) => {
                        setDraft((d) => ({ ...d, periodAmountStr: t }));
                        if (!isIncomePeriodAmountInvalid(t)) {
                            setShowAmountInvalid(false);
                        }
                    }}
                    onBlur={(e) => {
                        setShowAmountInvalid(
                            isIncomePeriodAmountInvalid(
                                draftRef.current.periodAmountStr,
                            ),
                        );
                        if (
                            !isFocusMovingWithinMoneyMapTableRow(
                                tableRowRef.current as unknown as Element | null,
                                e,
                            )
                        ) {
                            void tryPersist(draftRef.current);
                        }
                    }}
                    allowDecimals={false}
                    disabled={disabled}
                />
            </YStack>
            <YStack {...incomeTablePayCycleColumnProps}>
                <GroveMoneyMapPickerField
                    size="regular"
                    value={draft.payCycle}
                    options={payCyclePickerItems}
                    disabled={disabled}
                    onTriggerBlur={persistIfLeavingRow}
                    onChange={(v) => {
                        const next = { ...draft, payCycle: v };
                        setDraft(next);
                        void tryPersist(next);
                    }}
                />
            </YStack>
            <YStack {...incomeTableIncomeTypeColumnProps}>
                <GroveMoneyMapPickerField
                    size="regular"
                    value={draft.type}
                    options={incomeTypePickerItems}
                    disabled={disabled}
                    onTriggerBlur={persistIfLeavingRow}
                    onChange={(v) => {
                        const next = { ...draft, type: v };
                        setDraft(next);
                        void tryPersist(next);
                    }}
                />
            </YStack>
            <XStack
                width={INCOME_TABLE_ACTIONS_OUTER_WIDTH}
                flexShrink={0}
                minHeight={44}
                alignItems="flex-start"
                justifyContent="flex-end"
                paddingTop={4}
            >
                {isRowSaving ? (
                    <YStack
                        width={28}
                        minHeight={32}
                        alignItems="center"
                        justifyContent="center"
                        paddingTop={2}
                    >
                        <Spinner size={SpinnerSize.SMALL} />
                    </YStack>
                ) : (
                    <IconButton
                        variant="tertiary"
                        icon={MoreHorizontalIcon}
                        disabled={disabled}
                        onPress={onOpenDetails}
                    />
                )}
            </XStack>
        </XStack>
    );
};
